import * as React from "react";
import { CardActions, CardContent, CardHeader, Grid, Typography } from "@material-ui/core";
import { Card } from "react-bootstrap";
import { Skeleton } from "@material-ui/lab";
import { useTranslation } from 'react-i18next';
import { IFormOrganism } from "../IFormOrganism";
import { IData } from "../../../store/contactpoint/ContactpointItem";
import { ContactpointIcon } from "../../atoms/icons/ContactpointIcon";
import { ContactpointTextInput } from "../../molecules/forms/ContactpointTextInput";
import { AppointmentRequiredInput } from "../../molecules/forms/AppointmentRequiredInput";
import { ContractDateSelect } from "../../molecules/forms/ContractDateSelect";
import { CitySelect } from "../../molecules/forms/CitySelect";

// All the incoming values
interface IProps {
}

// Outgoing functions
interface IDispatch {
    onDirtyChange?: () => void;
}

interface IValidationResult {
    name: boolean;
    address: boolean;
    city: boolean;
    contactpersonInfo: boolean;
    appointmentInfo: boolean;
    contractInfo: boolean;
}

// Actual Component
export type AllProps = IProps & IDispatch & IFormOrganism<IData>;
export const ContactpointForm = (props: AllProps) => {
    const { t: tContactpoint } = useTranslation('contactpoint');
    const { t: tCommon } = useTranslation('common');
    const { t: tDatatypes } = useTranslation('datatypes');
    const [data, setData] = React.useState(props.data);
    const defaultValidationResult = !props.prevalidate;
    const [validationResult, setValidationResult] = React.useState<IValidationResult>({
        name: defaultValidationResult,
        address: defaultValidationResult,
        city: defaultValidationResult,
        contactpersonInfo: defaultValidationResult,
        appointmentInfo: defaultValidationResult,
        contractInfo: defaultValidationResult
    });
    const loading = props.loading;
    const disabled = props.disabled;

    // Sync local data state with props.data when it changes
    React.useEffect(() => {
        setData(props.data);
    }, [props.data]);

    const update = (newData: Partial<IData>, vr: Partial<IValidationResult>) => {
        setData(prev => {
            const updated = { ...prev, ...newData };

            setValidationResult(prevV => {
                const newValidationResult = { ...prevV, ...vr };
                const result = Object.values(newValidationResult).every(v => v === true);
                props.onUpdate && props.onUpdate(updated, result);
                return newValidationResult;
            });

            return updated;
        });
        props.onDirtyChange?.();
    };

    return <Card>
        {props.loading ? 
            <CardHeader title={<Skeleton variant="text" />} subheader={<Skeleton variant="text" />} avatar={<Skeleton variant="circle" />} /> : 
            <CardHeader title={tContactpoint('title')} subheader={data.name || tContactpoint('title')} avatar={<ContactpointIcon />} />
        }
        <CardContent>
            <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start" spacing={2}>
                
                {/* Basic Information */}
                <Grid item xs={12} md={6} lg={4}>
                    <Typography gutterBottom variant="h5" component="h2">
                        {tContactpoint('basisInformatieTitle')}
                    </Typography>
                    
                    <ContactpointTextInput
                        label={tDatatypes('naamLabel')}
                        value={data.name}
                        onChange={(value, isValid) => update({ name: value || "" }, { name: isValid })}
                        loading={loading}
                        disabled={disabled}
                        required
                        prevalidate={props.prevalidate}
                    />
                    
                    <ContactpointTextInput
                        label={tDatatypes('adresLabel')}
                        value={data.address || ""}
                        onChange={(value, isValid) => update({ address: value || "" }, { address: isValid })}
                        loading={loading}
                        disabled={disabled}
                        required
                        prevalidate={props.prevalidate}
                    />
                    
                    <CitySelect
                        selectedCityId={data.city?.cityId}
                        onChange={(city, isValid) => update({ city: { cityId: city?.id ?? "", cityName: city?.name ?? null, postalCode: city?.postalCode ?? null, province: null } }, { city: isValid })}
                        loading={loading}
                        disabled={disabled}
                        prevalidate={props.prevalidate}
                    />
                    
                    <ContactpointTextInput
                        label={tContactpoint('contactpointPlusLabel')}
                        value={data.contactpointPlus || ""}
                        onChange={(value, isValid) => update({ contactpointPlus: value }, { contactpersonInfo: isValid })}
                        loading={loading}
                        disabled={disabled}
                        prevalidate={props.prevalidate}
                    />
                </Grid>
                
                {/* Contact Person Information */}
                <Grid item xs={12} md={6} lg={4}>
                    <Typography gutterBottom variant="h5" component="h2">
                        {tContactpoint('contactpersoonTitle')}
                    </Typography>
                    
                    <ContactpointTextInput
                        label={tContactpoint('salutationLabel')}
                        value={data.salutation || ""}
                        onChange={(value, isValid) => update({ salutation: value }, { contactpersonInfo: isValid })}
                        loading={loading}
                        disabled={disabled}
                        prevalidate={props.prevalidate}
                    />
                    
                    <ContactpointTextInput
                        label={tDatatypes('firstnameLabel')}
                        value={data.contactpersonFirstname || ""}
                        onChange={(value, isValid) => update({ contactpersonFirstname: value }, { contactpersonInfo: isValid })}
                        loading={loading}
                        disabled={disabled}
                        prevalidate={props.prevalidate}
                    />
                    
                    <ContactpointTextInput
                        label={tDatatypes('lastnameLabel')}
                        value={data.contactpersonLastname || ""}
                        onChange={(value, isValid) => update({ contactpersonLastname: value }, { contactpersonInfo: isValid })}
                        loading={loading}
                        disabled={disabled}
                        prevalidate={props.prevalidate}
                    />
                    
                    <ContactpointTextInput
                        label={tDatatypes('functionLabel')}
                        value={data.contactpersonFunction || ""}
                        onChange={(value, isValid) => update({ contactpersonFunction: value }, { contactpersonInfo: isValid })}
                        loading={loading}
                        disabled={disabled}
                        prevalidate={props.prevalidate}
                    />
                    
                    <ContactpointTextInput
                        label={tContactpoint('emailLabel')}
                        value={data.contactpersonEmail || ""}
                        onChange={(value, isValid) => update({ contactpersonEmail: value }, { contactpersonInfo: isValid })}
                        loading={loading}
                        disabled={disabled}
                        prevalidate={props.prevalidate}
                    />
                    
                    <ContactpointTextInput
                        label={tContactpoint('email2Label')}
                        value={data.contactpersonEmail2 || ""}
                        onChange={(value, isValid) => update({ contactpersonEmail2: value }, { contactpersonInfo: isValid })}
                        loading={loading}
                        disabled={disabled}
                        prevalidate={props.prevalidate}
                    />
                    
                    <ContactpointTextInput
                        label={tContactpoint('telefoonLabel')}
                        value={data.contactpersonTelephone || ""}
                        onChange={(value, isValid) => update({ contactpersonTelephone: value }, { contactpersonInfo: isValid })}
                        loading={loading}
                        disabled={disabled}
                        prevalidate={props.prevalidate}
                    />
                    
                    <ContactpointTextInput
                        label={tContactpoint('mobieltLabel')}
                        value={data.contactpersonMobile || ""}
                        onChange={(value, isValid) => update({ contactpersonMobile: value }, { contactpersonInfo: isValid })}
                        loading={loading}
                        disabled={disabled}
                        prevalidate={props.prevalidate}
                    />
                    
                    <ContactpointTextInput
                        label={tContactpoint('contactpersoon2Label')}
                        value={data.contactperson2 || ""}
                        onChange={(value, isValid) => update({ contactperson2: value }, { contactpersonInfo: isValid })}
                        loading={loading}
                        disabled={disabled}
                        prevalidate={props.prevalidate}
                    />
                    
                    <ContactpointTextInput
                        label={tContactpoint('contactDetailsLabel')}
                        value={data.contactDetails || ""}
                        onChange={(value, isValid) => update({ contactDetails: value }, { contactpersonInfo: isValid })}
                        loading={loading}
                        disabled={disabled}
                        prevalidate={props.prevalidate}
                    />
                </Grid>
                
                {/* Facilities Information */}
                <Grid item xs={12} md={6} lg={4}>
                    <Typography gutterBottom variant="h5" component="h2">
                        {tContactpoint('faciliteitenTitle')}
                    </Typography>
                    
                    <AppointmentRequiredInput
                        label={tContactpoint('afspraakVereistLabel')}
                        value={data.appointmentRequired}
                        onChange={(value, isValid) => update({ appointmentRequired: value || false }, { appointmentInfo: isValid })}
                        loading={loading}
                        disabled={disabled}
                        prevalidate={props.prevalidate}
                    />
                    
                    <ContactpointTextInput
                        label={tContactpoint('beschikbaarheidLabel')}
                        value={data.availability || ""}
                        onChange={(value, isValid) => update({ availability: value }, { appointmentInfo: isValid })}
                        loading={loading}
                        disabled={disabled}
                        prevalidate={props.prevalidate}
                    />
                    
                    <ContactpointTextInput
                        label={tContactpoint('afspraakInformatieLabel')}
                        value={data.appointmentInformation || ""}
                        onChange={(value, isValid) => update({ appointmentInformation: value }, { appointmentInfo: isValid })}
                        loading={loading}
                        disabled={disabled}
                        prevalidate={props.prevalidate}
                    />
                    
                    <ContactpointTextInput
                        label={tContactpoint('gesprekruimteLabel')}
                        value={data.conversationSpace || ""}
                        onChange={(value, isValid) => update({ conversationSpace: value }, { appointmentInfo: isValid })}
                        loading={loading}
                        disabled={disabled}
                        prevalidate={props.prevalidate}
                    />
                    
                    <ContactpointTextInput
                        label={tContactpoint('internetLabel')}
                        value={data.internet || ""}
                        onChange={(value, isValid) => update({ internet: value }, { appointmentInfo: isValid })}
                        loading={loading}
                        disabled={disabled}
                        prevalidate={props.prevalidate}
                    />
                    
                    <ContactpointTextInput
                        label={tContactpoint('drankLabel')}
                        value={data.drinking || ""}
                        onChange={(value, isValid) => update({ drinking: value }, { appointmentInfo: isValid })}
                        loading={loading}
                        disabled={disabled}
                        prevalidate={props.prevalidate}
                    />
                    
                    <ContactpointTextInput
                        label={tContactpoint('keyLabel')}
                        value={data.key || ""}
                        onChange={(value, isValid) => update({ key: value }, { appointmentInfo: isValid })}
                        loading={loading}
                        disabled={disabled}
                        prevalidate={props.prevalidate}
                    />
                    
                    <ContactpointTextInput
                        label={tContactpoint('alarmLabel')}
                        value={data.alarm || ""}
                        onChange={(value, isValid) => update({ alarm: value }, { appointmentInfo: isValid })}
                        loading={loading}
                        disabled={disabled}
                        prevalidate={props.prevalidate}
                    />
                    
                    <ContactpointTextInput
                        label={tContactpoint('heatingLabel')}
                        value={data.heating || ""}
                        onChange={(value, isValid) => update({ heating: value }, { appointmentInfo: isValid })}
                        loading={loading}
                        disabled={disabled}
                        prevalidate={props.prevalidate}
                    />
                    
                    <ContactpointTextInput
                        label={tContactpoint('flipoverLabel')}
                        value={data.flipover || ""}
                        onChange={(value, isValid) => update({ flipover: value }, { appointmentInfo: isValid })}
                        loading={loading}
                        disabled={disabled}
                        prevalidate={props.prevalidate}
                    />
                </Grid>
                
                {/* Contract Information */}
                <Grid item xs={12} md={6} lg={4}>
                    <Typography gutterBottom variant="h5" component="h2">
                        {tContactpoint('contractTitle')}
                    </Typography>
                    
                    <AppointmentRequiredInput
                        label={tContactpoint('contractAvailableLabel')}
                        value={data.contractAvailable}
                        onChange={(value, isValid) => update({ contractAvailable: value || false }, { contractInfo: isValid })}
                        loading={loading}
                        disabled={disabled}
                        prevalidate={props.prevalidate}
                    />
                    
                    <AppointmentRequiredInput
                        label={tContactpoint('contractSavedLabel')}
                        value={data.contractSaved}
                        onChange={(value, isValid) => update({ contractSaved: value || false }, { contractInfo: isValid })}
                        loading={loading}
                        disabled={disabled}
                        prevalidate={props.prevalidate}
                    />
                    
                    <ContractDateSelect
                        label={tContactpoint('contractDateLabel')}
                        value={data.contractDate ? new Date(data.contractDate) : null}
                        onChange={(value, isValid) => update({ contractDate: value ? value.toISOString() : null }, { contractInfo: isValid })}
                        loading={loading}
                        disabled={disabled}
                        prevalidate={props.prevalidate}
                    />
                    
                    <ContactpointTextInput
                        label={tContactpoint('noContractReasonLabel')}
                        value={data.noContractReason || ""}
                        onChange={(value, isValid) => update({ noContractReason: value }, { contractInfo: isValid })}
                        loading={loading}
                        disabled={disabled}
                        prevalidate={props.prevalidate}
                    />
                </Grid>
            </Grid>
        </CardContent>
        <CardActions>
            {props.actions}
        </CardActions>
    </Card>;
}
